import { useCallback, useMemo, useState } from 'react';
import type { AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';

type UseWhitespaceAxesControlsParams = {
    xAxis: string;
    yAxis: string;
    crossSection: string;
    setXAxis: (value: string) => void;
    setYAxis: (value: string) => void;
    setCrossSection: (value: string) => void;
    applyXAxisDimensionConfig: (config: AxisConfiguration) => void;
    applyYAxisKpiConfig: (config: KpiAxisConfiguration) => void;
    applyYAxisDimensionConfig: (config: AxisConfiguration) => void;
    trackAxisChanged: (next: { xAxis?: string; yAxis?: string; crossSection?: string }) => void;
};

const useWhitespaceAxesControls = ({
    xAxis,
    yAxis,
    crossSection,
    setXAxis,
    setYAxis,
    setCrossSection,
    applyXAxisDimensionConfig,
    applyYAxisKpiConfig,
    applyYAxisDimensionConfig,
    trackAxisChanged,
}: UseWhitespaceAxesControlsParams) => {
    const [isCrossSectionOpen, setIsCrossSectionOpen] = useState(false);

    const handleAxisMenuOpenChange = useCallback(() => undefined, []);

    const handleXAxisChange = useCallback(
        (nextValue: string) => {
            if (nextValue === xAxis) return;
            trackAxisChanged({ xAxis: nextValue });
            setXAxis(nextValue);
        },
        [setXAxis, trackAxisChanged, xAxis],
    );

    const handleYAxisChange = useCallback(
        (nextValue: string) => {
            if (nextValue === yAxis) return;
            trackAxisChanged({ yAxis: nextValue });
            setYAxis(nextValue);
        },
        [setYAxis, trackAxisChanged, yAxis],
    );

    const handleXAxisConfigurationChange = useCallback(
        (config: AxisConfiguration) => {
            applyXAxisDimensionConfig(config);
        },
        [applyXAxisDimensionConfig],
    );

    const handleYAxisKpiConfigurationChange = useCallback(
        (config: KpiAxisConfiguration) => {
            applyYAxisKpiConfig(config);
        },
        [applyYAxisKpiConfig],
    );

    const handleYAxisDimensionConfigurationChange = useCallback(
        (config: AxisConfiguration) => {
            applyYAxisDimensionConfig(config);
        },
        [applyYAxisDimensionConfig],
    );

    const handleCrossSectionOpenChange = useCallback((open: boolean) => {
        setIsCrossSectionOpen(open);
    }, []);

    const handleCrossSectionSelect = useCallback(
        (option: string) => {
            if (option !== crossSection) {
                trackAxisChanged({ crossSection: option });
            }
            setCrossSection(option);
            setIsCrossSectionOpen(false);
        },
        [crossSection, setCrossSection, trackAxisChanged],
    );

    const axesControlState = useMemo(
        () => ({
            isCrossSectionOpen,
            handleAxisMenuOpenChange,
            handleXAxisChange,
            handleYAxisChange,
            handleXAxisConfigurationChange,
            handleYAxisKpiConfigurationChange,
            handleYAxisDimensionConfigurationChange,
            handleCrossSectionOpenChange,
            handleCrossSectionSelect,
        }),
        [
            handleAxisMenuOpenChange,
            handleCrossSectionOpenChange,
            handleCrossSectionSelect,
            handleXAxisChange,
            handleXAxisConfigurationChange,
            handleYAxisChange,
            handleYAxisDimensionConfigurationChange,
            handleYAxisKpiConfigurationChange,
            isCrossSectionOpen,
        ],
    );

    return axesControlState;
};

export default useWhitespaceAxesControls;
